import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fmtFCFA } from "../lib/format";
import { Import, Heading } from "lucide-react";

const KEY = "stems-estimate-clipboard";
const clean = (it) => ({ kind: it.kind || "item", item: it.item || "", description: it.description || "", unit: it.unit || "",
  quantity: it.kind === "section" ? "" : String(it.quantity ?? ""), unit_cost: it.kind === "section" ? "" : String(it.unit_cost ?? "") });

export const readClipboard = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
export const writeClipboard = (items) => localStorage.setItem(KEY, JSON.stringify(items.map(clean)));
export const cleanItems = (items) => items.map(clean);

const DocRow = ({ d, active, onClick }) => (
  <button onClick={onClick} data-testid={`import-doc-${d.id}`}
    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${active ? "border-[#1E3A8A] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
    <div className="flex justify-between gap-2"><span className="font-semibold">{d.number}</span><span className="text-xs text-slate-400">{d.estimate_type === "contract" ? "Quotation" : "Estimate"}</span></div>
    <div className="text-xs text-slate-500 truncate">{d.client}{d.title ? ` · ${d.title}` : ""}</div>
  </button>
);

export const ImportItemsDialog = ({ excludeId, onImport }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [doc, setDoc] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const { data: docs = [] } = useQuery({ queryKey: ["estimates", "all"], queryFn: () => api.get("/estimates").then((r) => r.data), enabled: open });
  const s = q.trim().toLowerCase();
  const list = docs.filter((d) => d.id !== excludeId && (!s || `${d.number} ${d.client} ${d.title}`.toLowerCase().includes(s)));
  const items = doc?.items || [];
  const choose = (d) => { setDoc(d); setPicked(new Set((d.items || []).map((_, i) => i))); };
  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const done = () => { onImport(items.filter((_, i) => picked.has(i))); setOpen(false); setDoc(null); setQ(""); };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="estimate-import-items"><Import className="h-4 w-4 mr-2" /> Import from…</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="import-items-dialog">
          <DialogHeader>
            <DialogTitle>Import items from another estimate or quotation</DialogTitle>
            <DialogDescription>Pick a document, tick the lines you want, and they're added to the end of this one. Base prices are kept; labour is applied by this page.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-[240px_1fr] gap-4">
            <div className="space-y-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number or client…" data-testid="import-search" />
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5 pr-1">
                {list.map((d) => <DocRow key={d.id} d={d} active={doc?.id === d.id} onClick={() => choose(d)} />)}
                {!list.length && <p className="text-sm text-slate-400">Nothing found.</p>}
              </div>
            </div>
            <div className="max-h-[56vh] overflow-y-auto rounded-lg border border-slate-100">
              {!doc ? <p className="p-4 text-sm text-slate-400">Select a document on the left.</p> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase sticky top-0">
                    <tr><th className="px-2 py-2 w-8"><input type="checkbox" checked={picked.size === items.length && items.length > 0} onChange={(e) => setPicked(e.target.checked ? new Set(items.map((_, i) => i)) : new Set())} data-testid="import-select-all" /></th>
                      <th className="px-2 py-2 text-left">Item</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">Base cost</th></tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${it.kind === "section" ? "bg-blue-50/60 font-semibold text-[#1E3A8A]" : ""}`}>
                        <td className="px-2 py-1.5"><input type="checkbox" checked={picked.has(i)} onChange={() => toggle(i)} data-testid={`import-line-${i}`} /></td>
                        <td className="px-2 py-1.5">{it.kind === "section" ? <span className="flex items-center gap-1.5 uppercase"><Heading className="h-3.5 w-3.5" />{it.item}</span> : it.item}</td>
                        <td className="px-2 py-1.5 text-right">{it.kind === "section" ? "" : it.quantity}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num">{it.kind === "section" ? "" : fmtFCFA(it.unit_cost || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={done} disabled={!picked.size || !doc} data-testid="import-confirm" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Add {picked.size || ""} line{picked.size === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
